// Pagina login portale clienti.
// Due modalità: email+password oppure magic link (solo email → messaggio "controlla email").
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { Mail, Lock, Zap, ArrowLeft, CheckCircle } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Alert } from '../../components/ui/Alert'
import { usePortalStore } from './portal.store'
import { portalApiCalls } from './portal.api'

// ---------------------------------------------------------------
// Schema validazione form
// ---------------------------------------------------------------

const passwordLoginSchema = z.object({
  email: z.string().email('Inserisci un indirizzo email valido'),
  password: z.string().min(6, 'La password deve avere almeno 6 caratteri'),
})

const magicLinkSchema = z.object({
  email: z.string().email('Inserisci un indirizzo email valido'),
})

type PasswordLoginData = z.infer<typeof passwordLoginSchema>
type MagicLinkData = z.infer<typeof magicLinkSchema>

// ---------------------------------------------------------------
// Componente
// ---------------------------------------------------------------

export function PortalLoginPage() {
  const navigate = useNavigate()
  const { setPortalAuth } = usePortalStore()
  const [mode, setMode] = useState<'password' | 'magic-link'>('password')
  const [magicLinkSent, setMagicLinkSent] = useState(false)

  // Form login con password
  const passwordForm = useForm<PasswordLoginData>({
    resolver: zodResolver(passwordLoginSchema),
  })

  // Form magic link
  const magicLinkForm = useForm<MagicLinkData>({
    resolver: zodResolver(magicLinkSchema),
  })

  // Mutation login password
  const loginMutation = useMutation({
    mutationFn: (data: PasswordLoginData) =>
      portalApiCalls.login({ email: data.email, password: data.password }),
    onSuccess: ({ data }) => {
      setPortalAuth({
        accessToken: data.accessToken,
        companyId: data.companyId,
        email: data.email,
        companyName: data.companyName,
      })
      navigate('/portal/dashboard')
    },
  })

  // Mutation magic link
  const magicLinkMutation = useMutation({
    mutationFn: (data: MagicLinkData) =>
      portalApiCalls.sendMagicLink(data.email),
    onSuccess: () => {
      setMagicLinkSent(true)
    },
  })

  const loginError = loginMutation.error
    ? loginMutation.error instanceof Error
      ? loginMutation.error.message
      : 'Credenziali non valide. Riprova.'
    : null

  const magicLinkError = magicLinkMutation.error
    ? magicLinkMutation.error instanceof Error
      ? magicLinkMutation.error.message
      : 'Errore invio magic link.'
    : null

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 py-12">
      {/* Card login */}
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--fc-primary)] shadow-md">
            <span className="text-xl font-bold text-white" aria-hidden="true">FC</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Portale Clienti</h1>
          <p className="mt-1 text-sm text-gray-500">
            Accedi per gestire i tuoi interventi
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          {/* Switcher modalità */}
          <div className="mb-6 grid grid-cols-2 rounded-lg bg-gray-100 p-1" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'password'}
              className={`rounded-md py-2 text-sm font-medium transition-all ${
                mode === 'password'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => {
                setMode('password')
                setMagicLinkSent(false)
              }}
            >
              <Lock className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
              Password
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'magic-link'}
              className={`rounded-md py-2 text-sm font-medium transition-all ${
                mode === 'magic-link'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => {
                setMode('magic-link')
                setMagicLinkSent(false)
              }}
            >
              <Zap className="mr-1.5 inline h-3.5 w-3.5" aria-hidden="true" />
              Link magico
            </button>
          </div>

          {/* Form password */}
          {mode === 'password' && (
            <form
              onSubmit={passwordForm.handleSubmit((d) => loginMutation.mutate(d))}
              noValidate
              className="space-y-4"
            >
              {loginError && (
                <Alert variant="error">{loginError}</Alert>
              )}
              <Input
                label="Email aziendale"
                type="email"
                autoComplete="email"
                leftIcon={<Mail className="h-4 w-4" />}
                error={passwordForm.formState.errors.email?.message}
                required
                {...passwordForm.register('email')}
              />
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                leftIcon={<Lock className="h-4 w-4" />}
                error={passwordForm.formState.errors.password?.message}
                required
                {...passwordForm.register('password')}
              />
              <Button
                type="submit"
                className="w-full"
                isLoading={loginMutation.isPending}
              >
                Accedi al portale
              </Button>
            </form>
          )}

          {/* Form magic link */}
          {mode === 'magic-link' && !magicLinkSent && (
            <form
              onSubmit={magicLinkForm.handleSubmit((d) => magicLinkMutation.mutate(d))}
              noValidate
              className="space-y-4"
            >
              {magicLinkError && (
                <Alert variant="error">{magicLinkError}</Alert>
              )}
              <p className="text-sm text-gray-500">
                Inserisci la tua email: ti invieremo un link per accedere
                direttamente, senza password.
              </p>
              <Input
                label="Email aziendale"
                type="email"
                autoComplete="email"
                leftIcon={<Mail className="h-4 w-4" />}
                error={magicLinkForm.formState.errors.email?.message}
                required
                {...magicLinkForm.register('email')}
              />
              <Button
                type="submit"
                className="w-full"
                isLoading={magicLinkMutation.isPending}
                leftIcon={<Zap className="h-4 w-4" />}
              >
                Invia link magico
              </Button>
            </form>
          )}

          {/* Messaggio magic link inviato */}
          {mode === 'magic-link' && magicLinkSent && (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
                <CheckCircle
                  className="h-8 w-8 text-[var(--fc-success)]"
                  aria-hidden="true"
                />
              </div>
              <h3 className="mb-2 text-base font-semibold text-gray-900">
                Controlla la tua email
              </h3>
              <p className="mb-4 text-sm text-gray-500">
                Abbiamo inviato un link di accesso alla tua email.
                Clicca sul link entro 60 minuti per accedere al portale.
              </p>
              <button
                type="button"
                className="flex items-center gap-1.5 mx-auto text-sm text-[var(--fc-primary)] hover:underline"
                onClick={() => setMagicLinkSent(false)}
              >
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                Non hai ricevuto? Riprova
              </button>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          Problemi di accesso? Contatta il supporto{' '}
          <a
            href="mailto:support@fieldcore.it"
            className="text-[var(--fc-primary)] hover:underline"
          >
            support@fieldcore.it
          </a>
        </p>
      </div>
    </div>
  )
}
